import {useEffect, useState} from "react";
import {Dish} from "../../Types/Dish";
import {deleteImage, deleteMenuItem, editMenuItem, uploadImage} from "../../Services/MenuItemService";
import {useFormValidation} from "../../Validation/UseFormValidation";
import {EditMenuItemScheme} from "../../Validation/FormSchemes/EditMenuItemScheme";
import {ErrorMessage} from "../../Components/ErrorMessage";
import {compressImage} from "../../Helpers/ImageCompressor";

type Props = {
  dish: Dish;
  onSaved: () => void;
};

function EditMenuItemBody({dish, onSaved}: Props) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
  });
  
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [existingPicturePath, setExistingPicturePath] = useState(dish.picturePath);
  const {errors, validateForm, resetErrors} = useFormValidation(EditMenuItemScheme);

  useEffect(() => {
    setFormData({
      name: dish.name,
      description: dish.description,
      price: String(dish.price),
    })
    setImageFile(null);
    setExistingPicturePath(dish.picturePath);
  }, [dish]);

  const handleChange = (e: { target: { name: any; value: any; }; }) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const characterCount = formData.description.length;

  const handleSave = async () => {
    try {
      if (!validateForm(formData)) return;

      let picturePath = existingPicturePath;
      if (imageFile) {
        const compressedFile = await compressImage(imageFile);
        picturePath = await uploadImage(compressedFile);
        
        if (existingPicturePath) {
          await deleteImage(existingPicturePath);
        }
      }
      
      await editMenuItem({
        id: dish.id!,
        menuItem: {
          name: formData.name,
          description: formData.description,
          madeOnDay: new Date().toISOString(),
          price: parseFloat(formData.price),
          picturePath,
        },
      });

      resetErrors();
      onSaved();
    } catch (err) {
      console.error(err);
      alert("Opslaan mislukt");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMenuItem(dish.id!)
      await deleteImage(existingPicturePath);
    } catch (err) {
      console.error(err);
      alert("Verwijderen mislukt");
    }
    
    onSaved();
  };

  return (
    <form>
      <h2 className="header-1">Gerecht veranderen</h2>

      <p className="popup-description">
        Het hier aanpassen van een gerecht veranderd het standaard gerecht in de opgeslagen gerechten. 
        Dat betekend dat gerechten in het weekmenu ook aangepast zullen worden.
      </p>

      <div className="form-item">
        <h3 className="input-label">Naam</h3>
        <input
          id="menu-name"
          className="input-field"
          name="name"
          value={formData.name}
          onChange={handleChange}
        />
        <ErrorMessage error={errors.name}/>
      </div>

      <div className="form-item">
        <ul className="text-area-labels">
          <li>
            <h3 className="input-label">Beschrijving</h3>
          </li>
          <li className="character-count">
            <h3>{characterCount}/500</h3>
          </li>
        </ul>
        <textarea
          id="menu-description"
          name="description"
          className="text-area"
          value={formData.description}
          onChange={handleChange}
        />
        <ErrorMessage error={errors.description}/>
      </div>

      <div className="form-item">
        <h3 className="input-label">Prijs</h3>
        <input
          id="menu-price"
          className="input-field input-field--small"
          name="price"
          value={formData.price}
          onChange={handleChange}
        />
        <ErrorMessage error={errors.price}/>
      </div>

      <div className="form-item">
        <h3 className="input-label">Foto</h3>
        <input
          id="menu-image"
          type="file"
          accept="image/*"
          onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
        />
      </div>

      <div className="double-buttons">
        <button type="button" onClick={handleDelete} className="button">
          Verwijderen
        </button>
        <button type="button" onClick={handleSave} className="button">
          Opslaan
        </button>
      </div>
    </form>
  );
}

export default EditMenuItemBody;
